import React, { useState, useCallback, useMemo } from "react";
import Modal from "../../../components/common/Modal";
import MenuItemForm from "./MenuItemForm";
import MenuItemOptionModal from "./MenuItemOptionModal";
import { useMenuItemsForm } from "../hooks/useMenuItemsForm";
import {
  ManageMenuItemModalProps,
  MenuItemImage,
} from "src/interfaces/menu-items-props.interface";

import { useStorePosLocales } from "src/hooks/useStorePosLocales";

const ManageMenuItemModalContent = ({
  onClose,
  menuCategoryId,
  menuCategoryName,
  menuItemToEdit,
  getMenuItems,
}: Omit<ManageMenuItemModalProps, "isOpen">) => {
  const [activeMainTab, setActiveMainTab] = useState<"details" | "options">("details");
  const [showOptionModal, setShowMenuItemOptionModal] = useState(false);
  const [editMenuItemOptionIndex, setEditMenuItemOptionIndex] = useState(-1);
  const [menuItemImage, setMenuItemImage] = useState<MenuItemImage | null>(
    null
  );

  const { enabledLocales } = useStorePosLocales();

  const { formik, addMenuItem, isAddingMenuItem } = useMenuItemsForm(
    menuCategoryId,
    menuItemToEdit,
    enabledLocales
  );

  const { values } = formik;

  const menuItemOptionIndex = useMemo(() => {
    return editMenuItemOptionIndex >= 0
      ? editMenuItemOptionIndex
      : values.itemOptionInputList?.length - 1;
  }, [editMenuItemOptionIndex, values.itemOptionInputList?.length]);

  const menuItemOptionModalTitle = useMemo(() => {
    return editMenuItemOptionIndex >= 0 ? "Edit Option" : "Add Option";
  }, [editMenuItemOptionIndex]);

  const handleCloseModal = useCallback(
    (shouldReload?: boolean) => {
      if (shouldReload) {
        getMenuItems();
      }
      onClose();
    },
    [getMenuItems, onClose]
  );

  const handleMenuItemOptionModal = useCallback(
    (menuItemOptionIndex: number) => {
      setEditMenuItemOptionIndex(menuItemOptionIndex);
      setShowMenuItemOptionModal(true);
    },
    []
  );

  const handleCloseMenuItemOptionModal = useCallback(() => {
    setEditMenuItemOptionIndex(-1);
    setShowMenuItemOptionModal(false);
  }, []);

  const optionsCount = formik.values.itemOptionInputList?.length ?? 0;
  const headerTabs = (
    <div className="flex items-center gap-1 rounded-lg border border-[#F1F1F2] p-0.5 bg-white">
      <button
        type="button"
        onClick={() => setActiveMainTab("details")}
        className={`h-[38px] px-3 text-xs font-medium rounded-md transition-all outline-none focus:ring-0 flex items-center ${
          activeMainTab === "details"
            ? "bg-[#FD7657] text-white shadow-sm"
            : "bg-white text-[#6b7280] hover:bg-[#FEF5F4] hover:text-[#FD7657]"
        }`}
      >
        Details
      </button>
      <button
        type="button"
        onClick={() => setActiveMainTab("options")}
        className={`h-[38px] px-3 text-xs font-medium rounded-md transition-all outline-none focus:ring-0 flex items-center gap-1.5 ${
          activeMainTab === "options"
            ? "bg-[#FD7657] text-white shadow-sm"
            : "bg-white text-[#6b7280] hover:bg-[#FEF5F4] hover:text-[#FD7657]"
        }`}
      >
        Options
        {optionsCount > 0 && (
          <span
            className={`text-xs font-medium px-1.5 py-0.5 rounded ${
              activeMainTab === "options"
                ? "bg-white/20 text-white"
                : "bg-[#E5E5E5] text-[#6b7280]"
            }`}
          >
            {optionsCount}
          </span>
        )}
      </button>
    </div>
  );

  return (
    <>
      <Modal
        isOpen
        onClose={() => handleCloseModal(false)}
        title={menuItemToEdit ? "Edit Item" : "Add Item"}
        headerRight={headerTabs}
        width="960px"
        maxWidth="960px"
        minWidth="10px"
        maxHeight="90dvh"
        contentClassName="px-4 py-3 sm:px-8"
      >
        <MenuItemForm
          formik={formik}
          menuCategoryId={menuCategoryId}
          menuCategoryName={menuCategoryName}
          getMenuItems={getMenuItems}
          menuItemToEdit={menuItemToEdit}
          menuItemImage={menuItemImage}
          setMenuItemImage={setMenuItemImage}
          addMenuItem={addMenuItem}
          isAddingMenuItem={isAddingMenuItem}
          onCloseModal={handleCloseModal}
          onOpenMenuItemOptionModal={handleMenuItemOptionModal}
          activeMainTab={activeMainTab}
          setActiveMainTab={setActiveMainTab}
        />
      </Modal>

      {showOptionModal && (
        <MenuItemOptionModal
          formik={formik}
          isOpen={showOptionModal}
          getMenuItems={getMenuItems}
          onClose={handleCloseMenuItemOptionModal}
          menuItemCorrelationId={menuItemToEdit?.correlationId}
          menuItemOptionModalTitle={menuItemOptionModalTitle}
          menuItemOptionIndex={menuItemOptionIndex}
        />
      )}
    </>
  );
};

const ManageMenuItemModal = ({ isOpen, ...props }: ManageMenuItemModalProps) => {
  if (!isOpen) return null;
  return <ManageMenuItemModalContent {...props} />;
};

export default ManageMenuItemModal;
